import {
  Combobox,
  Group,
  Pill,
  PillsInput,
  Popover,
  ScrollArea,
  UnstyledButton,
  useCombobox,
} from '@mantine/core';
import { useDisclosure, useElementSize, useMergedRef } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import classNames from '../ComboboxMultiSelect/ComboboxMultiSelect.module.scss';
import { ZorroActionIcon } from '../Icon';
import { brand } from '../theme';
import { BadgeColor, Badge as V2Badge } from '../v2/Badge';

type EnumConfigEntry = {
  label: string;
  color: BadgeColor;
  icon?: ReactNode;
};

type Props<V extends string> = {
  enumConfig: Record<V, EnumConfigEntry>;
  values: V[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: ReactNode;
};

const OverflowPopover = <V extends string>({
  values,
  enumConfig,
}: {
  values: V[];
  enumConfig: Record<V, EnumConfigEntry>;
}) => {
  const [opened, { toggle }] = useDisclosure();

  return (
    <Popover
      opened={opened}
      onChange={toggle}
      position="top"
      withArrow
      shadow="md"
    >
      <Popover.Target>
        <UnstyledButton
          onClick={(event) => {
            event.stopPropagation();
            toggle();
          }}
        >
          <V2Badge color="gray">{`+${values.length}`}</V2Badge>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Group gap="xs">
          {values.map((value) => {
            const { label, color, icon } = enumConfig[value];
            return (
              <V2Badge key={String(value)} color={color} leftSection={icon}>
                {label}
              </V2Badge>
            );
          })}
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

export const ZorroDataTableActionCell = <V extends string>({
  enumConfig,
  values,
  isOpen,
  onOpenChange,
  children,
}: Props<V>) => {
  const [selectedValues, setSelectedValues] = useState<V[]>([]);
  const [popoverValues, setPopoverValues] = useState<V[]>([]);
  const [indexDisplayFrom, setIndexDisplayFrom] = useState<number>();

  const combobox = useCombobox({
    opened: isOpen,
    onOpenedChange: onOpenChange,
  });

  const visibleValuesElementRef = useRef<HTMLDivElement>(null);
  const { ref: containerRef, width: containerWidth } = useElementSize();
  const { ref: visibleValuesSizeRef, width: visibleValuesWidth } =
    useElementSize();
  const visibleValuesRef = useMergedRef(
    visibleValuesSizeRef,
    visibleValuesElementRef
  );

  const checkIndexDisplayFrom = useCallback(
    (selectedItems: V[], index: number) => {
      if (indexDisplayFrom === undefined || indexDisplayFrom === index) {
        setIndexDisplayFrom(index);
        setPopoverValues(selectedItems.slice(index));
      }

      if (indexDisplayFrom !== undefined && indexDisplayFrom !== index) {
        setIndexDisplayFrom(index);
        setPopoverValues(selectedItems.slice(index));
      }
    },
    [indexDisplayFrom]
  );

  const calculatePopoverElements = useCallback(
    (selectedItems: V[]) => {
      if (!visibleValuesElementRef.current) {
        return;
      }

      let totalWidth = 0;
      let fitCount = 0;
      const minInputWidth = 100;

      const elmsValues = Array.from(
        visibleValuesElementRef.current.children
      ) as HTMLElement[];

      elmsValues?.forEach((elmValue) => {
        elmValue.style.display = 'inline-flex';
      });

      for (const elmValue of elmsValues) {
        const valueWidth = elmValue.offsetWidth;

        if (totalWidth + valueWidth < containerWidth - minInputWidth) {
          totalWidth += valueWidth;
          fitCount += 1;
        } else {
          for (
            let pillIndex = fitCount;
            pillIndex < elmsValues.length;
            pillIndex++
          ) {
            if (pillIndex !== 0) {
              elmsValues[pillIndex].style.display = 'none';
            }
          }
          break;
        }
      }

      if (fitCount > 0) {
        checkIndexDisplayFrom(selectedItems, fitCount);
      }
    },
    [
      visibleValuesWidth,
      containerWidth,
      visibleValuesElementRef,
      checkIndexDisplayFrom,
    ]
  );

  useLayoutEffect(() => {
    setSelectedValues(values);
  }, [values]);

  useLayoutEffect(() => {
    if (selectedValues.length > 0) {
      calculatePopoverElements(selectedValues);
    } else {
      setIndexDisplayFrom(undefined);
      setPopoverValues([]);
    }
  }, [selectedValues, calculatePopoverElements]);

  return (
    <Combobox store={combobox} width="max-content" position="bottom-start">
      <Combobox.Target ref={containerRef}>
        <PillsInput
          size="md"
          styles={{
            input: {
              border: 'unset',
              padding: 0,
              background: 'transparent',
            },
          }}
          onClick={() => onOpenChange(!isOpen)}
          rightSection={
            <Group gap="0">
              {popoverValues.length > 0 && (
                <OverflowPopover
                  values={popoverValues}
                  enumConfig={enumConfig}
                />
              )}
              <ZorroActionIcon size="md" isTransparent>
                <IconChevronDown
                  className={classNames['chevron']}
                  size={20}
                  color={brand.zorroGray400}
                />
              </ZorroActionIcon>
            </Group>
          }
          classNames={classNames}
          pointer
        >
          <Pill.Group className={classNames['pill-group']}>
            {selectedValues.length > 0 && (
              <Group
                ref={visibleValuesRef}
                gap="sm"
                wrap="nowrap"
                className={classNames['pills-wrapper']}
              >
                {selectedValues.map((value) => {
                  const { label, color, icon } = enumConfig[value];
                  return (
                    <V2Badge
                      key={String(value)}
                      color={color}
                      leftSection={icon}
                    >
                      {label}
                    </V2Badge>
                  );
                })}
              </Group>
            )}
          </Pill.Group>
        </PillsInput>
      </Combobox.Target>

      <Combobox.Dropdown>
        <ScrollArea.Autosize type="scroll" mah={360}>
          {isOpen && children}
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  );
};
